<template>
    <div class="atreemo-dynamic-input">
        <component
            :is="component"
            v-bind="inputProps"
            v-model="valueProxy"
        />

        <p
            v-if="link && text"
            class="link"
        >
            <a
                :href="link"
                target="_blank"
                rel="noopener"
            >
                {{ text }}
            </a>
        </p>
    </div>
</template>

<script>
    /* eslint-disable @netsells/require-jsdoc-except/require-jsdoc */
    const SelectInput = () => import('../../atoms/inputs/SelectInput/SelectInput');
    const TextInput = () => import('../../atoms/inputs/TextInput/TextInput');
    const EmailInput = () => import('../../atoms/inputs/EmailInput/EmailInput');
    const TelInput = () => import('../../atoms/inputs/TelInput/TelInput');
    const TextareaInput = () => import('../../atoms/inputs/TextareaInput/TextareaInput');
    const FileInput = () => import('../../atoms/inputs/FileInput/FileInput');
    const RatingInput = () => import('../../atoms/inputs/RatingInput/RatingInput');
    const DateInput = () => import('../../atoms/inputs/DateInput/DateInput');
    const SingleCheckboxInput = () => import('../../atoms/inputs/SingleCheckboxInput/SingleCheckboxInput');
    const MarkdownContent = () => import('../../utils/MarkdownContent/MarkdownContent');
    /* eslint-enable @netsells/require-jsdoc-except/require-jsdoc */

    export default {
        name: 'atreemo-dynamic-input',

        components: {
            TelInput,
            EmailInput,
            TextInput,
            SelectInput,
            TextareaInput,
            FileInput,
            RatingInput,
            DateInput,
            SingleCheckboxInput,
            MarkdownContent,
        },

        props: {
            value: {
                type: [
                    String,
                    Number,
                    Date,
                    Boolean,
                    typeof File !== 'undefined'
                        ? File
                        : false,
                ].filter(Boolean),
                default: null,
            },

            options: {
                type: Array,
                default: () => ([]),
            },

            id: {
                type: [Number, String],
                required: true,
            },

            type: {
                type: String,
                required: true,
            },

            link: {
                type: String,
                default: '',
            },

            text: {
                type: String,
                default: '',
            },

            label: {
                type: String,
                required: true,
            },

            required: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            valueProxy: {
                get() {
                    let val = this.value;

                    if (this.type === 'rating' && val) {
                        val = this.options
                            .find(({ value }) => Number(value) === Number(val))
                            ?.text;

                        val = Number(val);
                    }

                    return val;
                },

                set(val) {
                    if (this.type === 'rating') {
                        val = this.options
                            .find(({ text }) => Number(text) === Number(val))
                            ?.value;
                    }

                    this.$emit('input', val);
                },
            },

            /**
             * Return the component to render.
             *
             * @returns {string}
             */
            component() {
                return {
                    select: 'select-input',
                    text: 'text-input',
                    tel: 'tel-input',
                    email: 'email-input',
                    textarea: 'textarea-input',
                    file: 'file-input',
                    rating: 'rating-input',
                    date: 'date-input',
                    'single-checkbox': 'single-checkbox-input',
                    html: 'markdown-content',
                }[this.type];
            },

            /**
             * Return the props to apply to the input component.
             *
             * @returns {{name: string, label: string, required: boolean, options: Array|undefined, placeholder: string|undefined}}
             */
            inputProps() {
                if (this.type === 'html') {
                    return {
                        content: this.label,
                    };
                }

                const props = {
                    label: this.label,
                    name: `question-${ this.id }`,
                    required: false,
                };

                if (this.type === 'select') {
                    props.options = this.options;
                    props.placeholder = this.$snippet('select_input_placeholder', 'please select…');
                }

                if (this.type === 'single-checkbox') {
                    props.checkedValue = this.options[0].value;
                }

                if (['text', 'email', 'tel'].includes(this.type)) {
                    props.placeholder = this.$snippet('text_input_placeholder');
                }

                if (this.type === 'textarea') {
                    props.placeholder = this.$snippet('textarea_input_placeholder');
                }

                if (!['file', 'tel'].includes(this.type)) {
                    props.required = true;
                }

                return props;
            },
        },
    };
</script>

<style lang="scss" scoped>
    .link {
        font-size: 0.75em;
    }
</style>
